import { useEffect, useState, useCallback } from 'react'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface Contract {
  id: string
  name: string
  client_id: string
  client_name?: string
  type: string
  billing_cycle: string
  amount: number
  status: string
  start_date: string
  end_date: string | null
  renewal_date: string | null
  created_at: string
}

interface Dashboard {
  mrr: number
  arr: number
  active_count: number
  avg_value: number
  growth_trend: number
}

export default function Contracts() {
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('rmr_management')
  const { toast, toastError } = useToast()

  const [contracts, setContracts] = useState<Contract[]>([])
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [formData, setFormData] = useState({
    name: '', client_id: '', type: 'monitoring', amount: 0,
    billing_cycle: 'monthly', start_date: new Date().toISOString().split('T')[0],
  })
  const [clients, setClients] = useState<{ id: string; company_name: string }[]>([])

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      const [contractsRes, dashboardRes] = await Promise.all([
        api.get<{ data: Contract[] }>(`/rmr?${params}`).catch(() => ({ data: [] })),
        api.get<Dashboard>('/rmr/dashboard').catch(() => null),
      ])
      setContracts(contractsRes.data || [])
      if (dashboardRes) setDashboard(dashboardRes)
    } catch (err) {
      toastError('Failed to load contracts', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, toastError])

  useEffect(() => {
    if (enabled) loadData()
  }, [enabled, loadData])

  // Load clients when drawer opens
  useEffect(() => {
    if (!drawerOpen) return
    api.get<{ data: { id: string; company_name: string }[] }>('/clients?limit=100')
      .then(r => setClients(r.data || []))
      .catch(() => {})
  }, [drawerOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/rmr', formData)
      toast('Contract created')
      setDrawerOpen(false)
      setFormData({ name: '', client_id: '', type: 'monitoring', amount: 0, billing_cycle: 'monthly', start_date: new Date().toISOString().split('T')[0] })
      loadData()
    } catch (err) {
      toastError('Failed to create contract', err)
    } finally {
      setSaving(false)
    }
  }

  if (flagLoading) return <TableSkeleton cols={6} />
  if (!enabled) return <UpgradeBanner feature="Contracts & RMR" plan={requiredPlan} description="Manage recurring revenue contracts, track MRR/ARR, and monitor renewal dates." />

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Contracts</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Contract</Button>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'MRR', value: `$${Math.round(dashboard.mrr).toLocaleString()}`, color: 'var(--accent-h)' },
            { label: 'ARR', value: `$${Math.round(dashboard.arr).toLocaleString()}`, color: 'var(--accent)' },
            { label: 'Active', value: String(dashboard.active_count), color: 'var(--ok)' },
            { label: 'Avg Value', value: `$${Math.round(dashboard.avg_value).toLocaleString()}`, color: 'var(--info)' },
          ].map((s, i) => (
            <GlowCard key={s.label} style={{ padding: '12px 20px', minWidth: 100, textAlign: 'center', flex: 1 }} delay={100 + i * 80}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{s.label}</div>
            </GlowCard>
          ))}
        </div>
      )}

      <div className="mb-4">
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="max-w-[160px]"
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </div>

      {loading ? <TableSkeleton cols={7} /> : contracts.length === 0 ? (
        <EmptyState
          title="No contracts yet"
          description="Create your first recurring revenue contract."
          action={<Button onClick={() => setDrawerOpen(true)}>New Contract</Button>}
        />
      ) : (
        <GlowCard style={{ padding: 0 }}>
          <table className="v3-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client</th>
                <th>Type</th>
                <th>Billing</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
                <th>Renewal</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(c => (
                <tr key={c.id} style={{ cursor: 'default' }}>
                  <td style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-1)' }}>{c.name}</td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{c.client_name || '-'}</td>
                  <td style={{ fontSize: 12, textTransform: 'capitalize', color: 'var(--text-2)' }}>{c.type}</td>
                  <td style={{ fontSize: 12, textTransform: 'capitalize', color: 'var(--text-2)' }}>{c.billing_cycle}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 13 }}>${c.amount.toLocaleString()}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                    {c.renewal_date ? new Date(c.renewal_date).toLocaleDateString() : c.end_date ? new Date(c.end_date).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlowCard>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Contract">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Name *</label>
            <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="v3-input" required placeholder="Monthly Monitoring" />
          </div>
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Client</label>
            <div className="v3-select-wrap">
              <select value={formData.client_id} onChange={e => setFormData({ ...formData, client_id: e.target.value })} className="v3-select">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <span className="v3-select-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </span>
            </div>
          </div>
          <Select
            label="Type"
            value={formData.type}
            onChange={e => setFormData({ ...formData, type: e.target.value })}
            options={[
              { value: 'monitoring', label: 'Monitoring' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'subscription', label: 'Subscription' },
              { value: 'sla', label: 'SLA' },
              { value: 'other', label: 'Other' },
            ]}
          />
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Amount *</label>
            <input type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} className="v3-input" required />
          </div>
          <Select
            label="Billing Cycle"
            value={formData.billing_cycle}
            onChange={e => setFormData({ ...formData, billing_cycle: e.target.value })}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'semi_annual', label: 'Semi-Annual' },
              { value: 'annual', label: 'Annual' },
            ]}
          />
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Start Date *</label>
            <input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} className="v3-input" required />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            {saving ? 'Creating...' : 'Create Contract'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
